import { useReducer, useEffect, useCallback, useContext } from 'react';
import isEqual from 'lodash/isEqual';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { getStatusCodeGroup } from '@atlassian/jira-project-custom-roles/src/services/utils/index.tsx';
import MainProviderContext from '../access-page-context';
import type {
	RoleServiceState,
	RolesServiceProps,
	RoleServiceAction,
	RoleServiceDispatch,
} from './types';

export const getLatestRolesDescriptionUrl = (baseUrl: string, projectKey: string): string =>
	`${baseUrl}/rest/api/3/project/${projectKey}/roledetails?excludeConnectAddons=true`;

export const fetchRolesDetail = async (
	url: string,
	key: string,
	dispatch: RoleServiceDispatch,
): Promise<void> => {
	dispatch({ type: 'ROLESDETAIL_API_LOADING' });
	try {
		const data = await fetchJson(getLatestRolesDescriptionUrl(url, key));
		dispatch({ type: 'ROLESDETAIL_API_DONE', data });
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'fetchRolesDetail',
				packageName: 'jiraUsersAndRoles',
				teamName: 'olympus',
			},
			error,
			attributes: {
				key,
				statusCode: getStatusCodeGroup(error),
			},
			sendToPrivacyUnsafeSplunk: true,
		});
		dispatch({ type: 'ROLESDETAIL_API_ERROR', error });
	}
};

export const rolesReducer = (
	state: RoleServiceState,
	action: RoleServiceAction,
): RoleServiceState => {
	const initialLoadingState: RoleServiceState = { ...state, loading: true, error: null };
	switch (action.type) {
		case 'ROLESDETAIL_API_LOADING':
			return isEqual(state, { ...state, loading: true, data: null, error: null })
				? state
				: initialLoadingState;
		case 'ROLESDETAIL_API_DONE':
			// @ts-expect-error - TS2322 - Type 'JiraRoleDescription[] | undefined' is not assignable to type 'JiraRoleDescription[] | null'.
			return { ...state, loading: false, data: action.data, error: action.error || null };
		case 'ROLESDETAIL_API_ERROR':
			// @ts-expect-error - TS2322 - Type 'JiraError | undefined' is not assignable to type 'JiraError | null'.
			return { ...state, loading: false, data: action.data || null, error: action.error };
		default:
			return state;
	}
};

export const getInitialState = (): RoleServiceState => ({
	loading: true,
	data: null,
	error: null,
});

const RolesService = (props: RolesServiceProps) => {
	const { baseUrl, projectKey } = useContext(MainProviderContext);

	// @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
	const [state, dispatch] = useReducer<RoleServiceState, RoleServiceAction>(
		rolesReducer,
		getInitialState(),
	);

	const onRefetch = useCallback(
		async () => fetchRolesDetail(baseUrl, projectKey, dispatch),
		[baseUrl, projectKey, dispatch],
	);
	const { children } = props;
	useEffect(() => {
		fetchRolesDetail(baseUrl, projectKey, dispatch);
	}, [baseUrl, projectKey, dispatch]);
	// @ts-expect-error - TS2698 - Spread types may only be created from object types.
	return children({ ...state, onRefetch });
};

export default RolesService;
